// ScriptEditor — route /scripts/:id
//
// 3-panel editor surface for the LLM-generated draft. Left panel = project
// metadata (title, profile, topic, status, timestamps). Right panel = three
// SectionPanels (Outline, Hook, Draft) that the operator can collapse, copy,
// and regenerate per-section. Bottom bar surfaces an Export link (Bundle B).

import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, FileDown } from 'lucide-react'
import { ApiError, renderDownloadUrl } from '@/api/client'
import type { SectionName } from '@/api/types'
import { useConti, useDecompose, useRegenerateSection, useRender, useScript } from '@/hooks/useScript'
import SectionPanel from '@/components/SectionPanel'
import SceneBoard from '@/components/SceneBoard'
import { formatTime } from '@/lib/utils'
import { Badge, Button, Card } from '@/admin-design/_common'

export default function ScriptEditor() {
  const params = useParams<{ id: string }>()
  const id = Number(params.id)
  const valid = Number.isFinite(id) && id > 0

  const scriptQ = useScript(id, valid)
  const regenM = useRegenerateSection(id)
  const contiQ = useConti(id, valid)
  const decomposeM = useDecompose(id)
  const render = useRender(id, valid && Boolean(contiQ.data?.payload))

  // Track which section's regen is in flight so only that panel shows the
  // skeleton state. The mutation itself doesn't carry the section name in its
  // status tuple (it's the variables payload), so we read from .variables.
  const regeneratingSection: SectionName | null = useMemo(() => {
    if (!regenM.isPending) return null
    return regenM.variables ?? null
  }, [regenM.isPending, regenM.variables])

  if (!valid) {
    return (
      <NotFound message="Invalid script id." />
    )
  }
  if (scriptQ.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>
  }
  if (scriptQ.isError) {
    if (scriptQ.error instanceof ApiError && scriptQ.error.status === 404) {
      return <NotFound message="이 스크립트는 존재하지 않거나 삭제되었어요." />
    }
    return (
      <p className="p-6 text-sm text-destructive">
        로드 실패: {errorMessage(scriptQ.error)}
      </p>
    )
  }
  if (!scriptQ.data) {
    return <NotFound message="No data." />
  }

  const { project, payload } = scriptQ.data
  const outline = payload?.outline ?? ''
  const hook = payload?.hook ?? ''
  const draft = payload?.draft ?? ''
  const tokensUsed = payload?.tokens_used ?? 0
  const hasPayload = Boolean(payload && (outline || hook || draft))
  const renderStatus =
    render.status.isError &&
    render.status.error instanceof ApiError &&
    render.status.error.status === 404
      ? undefined
      : render.status.data
  const renderError =
    render.start.isError
      ? errorMessage(render.start.error)
      : render.status.isError &&
          !(render.status.error instanceof ApiError && render.status.error.status === 404)
        ? errorMessage(render.status.error)
        : renderStatus?.state === 'error'
          ? renderStatus.error
          : undefined

  const copy = (text: string) => navigator.clipboard.writeText(text)

  const onRegenerate = (section: SectionName) => () => {
    if (regenM.isPending) return
    regenM.mutate(section)
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <Link
          to="/scripts"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Scripts
        </Link>
        <h1 className="text-xl font-semibold truncate text-foreground">{project.title || 'Untitled'}</h1>
        <Badge className="font-mono text-[11px] rounded" aria-label="status">
          {project.status}
        </Badge>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
        <aside className="space-y-3">
          <MetaCard label="Channel">
            {project.channel_profile_name || `#${project.channel_profile_id}`}
          </MetaCard>
          <MetaCard label="Topic">
            {project.topic_title ||
              project.topic_id ||
              <em className="text-muted-foreground">no topic</em>}
          </MetaCard>
          <MetaCard label="Created">{formatTime(project.created_at)}</MetaCard>
          <MetaCard label="Updated">{formatTime(project.updated_at)}</MetaCard>
          <MetaCard label="Tokens used (last run)">
            <span className="font-mono">{tokensUsed.toLocaleString('en-US')}</span>
          </MetaCard>
          {regenM.isError && (
            <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
              {errorMessage(regenM.error)}
            </p>
          )}
        </aside>

        <div className="space-y-3">
          <SectionPanel
            title="Outline"
            body={outline}
            loading={regeneratingSection === 'outline'}
            canRegenerate={hasPayload}
            onRegenerate={onRegenerate('outline')}
            onCopy={() => copy(outline)}
            emptyPlaceholder="Outline 아직 생성되지 않음."
          />
          <SectionPanel
            title="Hook"
            body={hook}
            loading={regeneratingSection === 'hook'}
            canRegenerate={hasPayload}
            onRegenerate={onRegenerate('hook')}
            onCopy={() => copy(hook)}
            emptyPlaceholder="Hook 아직 생성되지 않음."
          />
          <SectionPanel
            title="Draft"
            body={draft}
            loading={regeneratingSection === 'draft'}
            canRegenerate={hasPayload}
            onRegenerate={onRegenerate('draft')}
            onCopy={() => copy(draft)}
            emptyPlaceholder="Draft 아직 생성되지 않음."
          />

          <footer className="flex items-center gap-2 border-t border-border pt-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                copy(
                  [
                    `# ${project.title}`,
                    '',
                    '## Hook',
                    hook,
                    '',
                    '## Outline',
                    outline,
                    '',
                    '## Draft',
                    draft,
                  ].join('\n'),
                )
              }
            >
              Copy all
            </Button>
            <a
              href={`/api/scripts/${project.id}/export?format=md`}
              className="ml-auto inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <FileDown size={12} /> Export
            </a>
          </footer>

          <SceneBoard
            conti={contiQ.data?.payload}
            loading={decomposeM.isPending}
            canDecompose={hasPayload && Boolean(draft)}
            onDecompose={() => {
              if (!decomposeM.isPending) decomposeM.mutate()
            }}
            error={decomposeM.isError ? errorMessage(decomposeM.error) : undefined}
            renderStatus={renderStatus}
            renderLoading={render.start.isPending}
            canRender={Boolean(contiQ.data?.payload) && !render.start.isPending && renderStatus?.state !== 'running'}
            onRender={() => {
              if (!render.start.isPending && renderStatus?.state !== 'running') render.start.mutate()
            }}
            downloadHref={renderDownloadUrl(project.id)}
            renderError={renderError}
          />
        </div>
      </div>
    </div>
  )
}

interface MetaCardProps {
  label: string
  children: React.ReactNode
}

function MetaCard({ label, children }: MetaCardProps) {
  return (
    <Card>
      <Card.Body className="px-3 py-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-card-foreground truncate">{children}</p>
      </Card.Body>
    </Card>
  )
}

function NotFound({ message }: { message: string }) {
  return (
    <div className="space-y-2 p-6">
      <p className="text-sm text-foreground">{message}</p>
      <Link to="/scripts" className="text-sm text-info hover:underline">
        Back to Scripts
      </Link>
    </div>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
